import React, { createContext, useEffect, useReducer, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookie from '../utils/Cookie';
import LoadingScreen from '../components/LoadingScreen';
import ToastHot from 'react-hot-toast';
import { TOKEN_KEY } from '../utils/constants';
import { CallWithAuth } from '../actions/apiAction';
import { ME_API_URL } from '../utils/apiUrl';
import { isInternalOnlyResponse } from '../utils/internalOnly';
import { useAuth } from '@clerk/clerk-react';
import { registerPushToken, unregisterPushToken } from '../utils/pushSetup';
import { SocketDisconnect, markInternalOnly, clearInternalOnly } from '../socket';

const AccessDenied = React.lazy(() => import('../views/auth/AccessDenied'));

const initialAuthState = {
  isAuthenticated: false,
  isInitialised: false,
  accessDenied: false,
  user: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'INITIALISE': {
      const { isAuthenticated, user, accessDenied = false } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        accessDenied,
        user
      };
    }
    case 'ACCESS_DENIED': {
      return {
        ...state,
        isAuthenticated: false,
        isInitialised: true,
        accessDenied: true,
        user: null,
      };
    }
    case 'LOGIN': {
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        accessDenied: false,
        user: null,
      };
    }
    case 'userUpdate': {
      return {
        ...state,
        user: action.payload,
      };
    }
    case 'REGISTER': {
      const { user } = action.payload;

      return {
        ...state,
        isAuthenticated: true,
        user: action.payload
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext({
  ...initialAuthState,
  Login: () => Promise.resolve(),
  Logout: () => { console.log('Logout') },
  updateUser: () => Promise.resolve(),
  RegisterUser: () => Promise.resolve(),
});

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const { getToken } = useAuth();

  const fetchToken = async () => {
    const token = await getToken({
      template: process.env.REACT_APP_CLEARK_TEMPLATE_ID
    });
    return token;
  };

  const Logout = async () => {
    sessionStorage.clear();
    // window.cookies.clear()
    Cookie.deleteCookie(TOKEN_KEY)
    // handleLogout(instance)
    clearInternalOnly();
    dispatch({ type: 'LOGOUT' });
    const accessToken = await fetchToken();
    unregisterPushToken(accessToken);
  };

  const refreshUser = async () => {
    try {
      const response = await CallWithAuth("GET", ME_API_URL);
      if (response?.data) {
        dispatch({ type: 'userUpdate', payload: response.data });
        return response.data;
      }
    } catch (err) {
      console.log('refreshUser error==>', err);
    }
    return null;
  };

  const updateUser = (user) => {
    dispatch({ type: 'userUpdate', payload: user })

  }

  const denyAccess = () => {
    markInternalOnly();
    SocketDisconnect();
    dispatch({ type: 'ACCESS_DENIED' });
  };

  useEffect(() => {
    const onDenied = () => dispatch({ type: 'ACCESS_DENIED' });
    window.addEventListener('tlk:internal-only', onDenied);
    return () => window.removeEventListener('tlk:internal-only', onDenied);
  }, []);

  useEffect(() => {
    const initialise = async () => {
      try {
        const accessToken = await fetchToken(); //Cookie.getCookie(TOKEN_KEY);
        if (accessToken) {
          Cookie.setCookie(TOKEN_KEY, accessToken, 1);
          // Clerk's session is ready before user.created writes the row.
          // Stay on the loading screen and poll. Redirecting to /login here
          // loops: GuestGuard sees the Clerk session and sends us back.
          const waitForUser = async () => {
            const deadline = Date.now() + 15000;
            let response = null;
            while (Date.now() < deadline) {
              response = await CallWithAuth("GET", ME_API_URL);
              const internalOnly = isInternalOnlyResponse(response)
                || (response?.status === 403 && process.env.REACT_APP_INTERNAL_ONLY === 'true');
              if (internalOnly) {
                return { internalOnly: true };
              }
              if (response?.status === 200 && response.data) {
                return { user: response.data };
              }
              if (response?.status !== 404) {
                break;
              }
              await new Promise((resolve) => setTimeout(resolve, 500));
            }
            return { user: null };
          };

          const me = await waitForUser();
          if (me.internalOnly) {
            denyAccess();
            return;
          }
          if (me.user) {
            localStorage.setItem("push_device_id", Math.random().toString(36).substring(2, 15));
            registerPushToken(accessToken).catch((err) => {
              console.warn("Push registration skipped:", err?.message || err);
            });

            dispatch({
              type: 'INITIALISE',
              payload: {
                isAuthenticated: true,
                user: me.user,
              }
            });
          } else {
            
            dispatch({
              type: 'INITIALISE',
              payload: {
                isAuthenticated: false,
                user: null,
              }
            });
          }
        } else {
          dispatch({
            type: 'INITIALISE',
            payload: {
              isAuthenticated: false,
              user: null,
            }
          });
        }
      } catch (err) {
        ToastHot.error("Something went wrong, Server error");
        console.log('error==>', err)
        dispatch({
          type: 'INITIALISE',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }


    };

    initialise();
  }, []);

  if (!state.isInitialised) {
    return <LoadingScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        Logout,
        updateUser,
        refreshUser,
      }}
    >
      {state.accessDenied ? (
        <Suspense fallback={<LoadingScreen />}>
          <AccessDenied />
        </Suspense>
      ) : children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
